/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import { types, getEnv, isAlive } from 'mobx-state-tree';

export default types
	.model('ImageCaptureModel', {
		id: types.string
	})
	.volatile(() => ({
		thumbnail: undefined,
		isMappingIncomplete: false,
		isDisabled: false,
		error: undefined,
		warning: undefined,

		// HACK
		// model here is temporary
		// the thumbnail service needs the model to update the thumbnail id
		// ideally there should be a clear API between this model and the service
		// (maybe through a callback or something like that).
		// ex: When the service needs to update something on the model, it
		// delegates that here and the widget model picks it up through a reaction
		model: undefined,

		readyPromise: undefined,
		feature: undefined
	}))
	.views((self) => ({
		isThumbnailReady() {
			// a thumbnail is considered ready when:
			// 1. we have a thumbnail - our job is done
			// 2. we don't have a thumbnail because the slot mapping is incomplete
			// 3. thumbnail is disabled on purpose
			// 4. we don't have a thumbnail because there's a warning or error when capturing the thumbnail
			return !!self.thumbnail || self.isMappingIncomplete || self.isDisabled || !!self.warning || !!self.error;
		}
	}))
	.actions((self) => {
		function fetchFeature() {
			const { dashboardApi } = getEnv(self);
			const widgetId = self.id;
			return dashboardApi.getCanvasWhenReady()
				.then(canvas => canvas.getWidgetWhenReady(widgetId))
				.then(widget => {
					if (widget) {
						return widget.getFeature('vis-image-capture');
					}
				});
		}

		function getService() {
			const { dashboardApi } = getEnv(self);

			return dashboardApi
				.getDashboardSvc('.Thumbnail');
		}

		return {
			beforeDestroy() {
				if (self.feature) {
					if (self.feature.isActivated()) {
						self.feature.deactivate();
					}
					self.feature = undefined;
				}
				self.setModel_HACK(undefined);
				self.readyPromise = undefined;
			},
			setThumbnail(thumbnail) {
				self.thumbnail = thumbnail;
			},
			setIsMappingIncomplete(isMappingIncomplete) {
				self.isMappingIncomplete = isMappingIncomplete;
			},
			setIsDisabled(isDisabled) {
				self.isDisabled = isDisabled;
			},
			setError(error) {
				self.error = error;
			},
			setWarning(warning) {
				self.warning = warning;
			},
			setFeature(feature) {
				self.feature = feature;
			},

			// HACK
			// model here is temporary
			// the thumbnail service needs the model to update the thumbnail id
			// ideally there should be a clear API between this model and the service
			// (maybe through a callback or something like that).
			// ex: When the service needs to update something on the model, it
			// delegates that here and the widget model picks it up through a reaction
			setModel_HACK(model) {
				self.model = model;
				if (model) {
					self.activateFeature();
				}
			},

			/**
			 * Activate feature
			 */
			activateFeature() {
				self.readyPromise = fetchFeature()
					.then(feature => {
						if (feature) {
							self.setFeature(feature);
							return feature.activate({
								captureHandlers: {
									success: (result) => {

										if (!isAlive(self)) {
											return Promise.resolve();
										}

										let thumbnail;
										let isMappingIncomplete;
										let isDisabled;
										if (typeof result === 'object') {
											thumbnail = result.thumbnail;
											isMappingIncomplete = !!result.isMappingIncomplete;
											isDisabled = !!result.isDisabled;
										} else {
											thumbnail = result;
											isMappingIncomplete = false;
											isDisabled = false;
										}

										self.setError(undefined);
										self.setIsMappingIncomplete(isMappingIncomplete);
										self.setIsDisabled(isDisabled);
										self.setThumbnail(thumbnail);

										return getService()
											.then(service => service.sync(self.model, thumbnail));
									},
									error: (error) => {

										if (!isAlive(self)) {
											return Promise.resolve();
										}

										self.setError(error);
										return getService()
											.then(service => service.setError(self.id, error));
									},
									warning: (warning) => {
										if (!isAlive(self)) {
											return Promise.resolve();
										}

										self.setWarning(warning);
										return getService()
											.then(service => service.setWarning(self.id, warning));
									},
									clear: () => {

										if (!isAlive(self)) {
											return;
										}

										if (self.model) {
											// void the cache id
											const cacheId = undefined;
											const options = {
												payloadData: {
													runtimeOnly: true
												}
											};
											self.model.set({
												thumbnailId: cacheId
											}, options);
										}
									},
									relink: () => {
										if (!isAlive(self)) {
											return;
										}

										if (self.model && self.model.thumbnailId) {
											// Wipe out the cache id when relink	
											self.model.set({
												thumbnailId: undefined
											});
										}
									}
								}
							});
						}
					});
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/components/image-capture/ImageCaptureModel.js